/**
 * Voice input for the host (2026-10-05). One spoken turn at a time: listen until the speaker
 * stops, then resolve with the words.
 *
 * - Desktop Chrome and Edge recognise speech themselves (the Web Speech API).
 * - Quest Browser has no recogniser, so the headset records the microphone, detects the end
 *   of speech from its loudness, resamples to 16 kHz and the ClinSpaceXR server transcribes it
 *   with Whisper (`POST /api/assistant/transcribe`). The audio goes to our server only.
 */
import { api } from '../lib/api.ts';
import { micMayPrompt } from '../audio/micPermission.ts';
import { dictationAvailable } from '../queries/dictation.ts';

export type ListenPhase = 'waiting' | 'hearing' | 'transcribing';

export interface ListenHandlers {
  onPhase?(phase: ListenPhase): void;
  /** Microphone loudness, 0..1, a few times a second. */
  onLevel?(level: number): void;
  onInterim?(text: string): void;
}

export interface Listener {
  readonly kind: 'browser' | 'server';
  /** Resolves with what was said ('' when nothing was), rejects on a microphone error. */
  listen(handlers?: ListenHandlers): Promise<string>;
  cancel(): void;
}

/** End-of-speech detection, pure so it is unit tested. Times in ms, levels as RMS. */
export const VAD = {
  /** Give up when nobody starts speaking within this long. */
  waitMs: 8000,
  /** Speech has ended after this much quiet. */
  silenceMs: 900,
  /** A turn never runs longer than this. */
  maxMs: 15000,
  /** Speech is louder than the room by this factor, and never quieter than minLevel. */
  factor: 3,
  minLevel: 0.012,
  /** Kept from before the speech started, so its first syllable is not cut. */
  prerollMs: 300,
};

export type VadEvent = 'start' | 'end' | 'timeout' | undefined;

/** Feeds chunk loudness through a little state machine: start of speech, end, or timeout. */
export class SpeechDetector {
  private floor = Infinity;
  private elapsed = 0;
  private quiet = 0;
  private speechMs = 0;
  speaking = false;

  /** The threshold a chunk must pass to count as speech. */
  get threshold(): number {
    return Math.max(VAD.minLevel, (Number.isFinite(this.floor) ? this.floor : 0) * VAD.factor);
  }

  push(level: number, ms: number): VadEvent {
    this.elapsed += ms;
    if (!this.speaking) {
      // The quietest recent chunk is the room's noise floor.
      this.floor = Math.min(this.floor, Math.max(level, 0.001));
      if (level > this.threshold && this.elapsed > 150) {
        this.speaking = true;
        this.quiet = 0;
        return 'start';
      }
      return this.elapsed > VAD.waitMs ? 'timeout' : undefined;
    }
    this.speechMs += ms;
    this.quiet = level > this.threshold * 0.8 ? 0 : this.quiet + ms;
    if (this.quiet >= VAD.silenceMs || this.speechMs >= VAD.maxMs) return 'end';
    return undefined;
  }
}

/** Linear resampling to 16 kHz, enough for speech recognition. */
export function resample(input: Float32Array, from: number, to = 16000): Float32Array {
  if (from === to) return input;
  const n = Math.floor((input.length * to) / from);
  const out = new Float32Array(n);
  const step = from / to;
  for (let i = 0; i < n; i++) {
    const x = i * step;
    const i0 = Math.floor(x);
    const i1 = Math.min(input.length - 1, i0 + 1);
    out[i] = input[i0]! + (input[i1]! - input[i0]!) * (x - i0);
  }
  return out;
}

export function levelOf(chunk: Float32Array): number {
  let sum = 0;
  for (const v of chunk) sum += v * v;
  return chunk.length ? Math.sqrt(sum / chunk.length) : 0;
}

/* ---------- The browser's own recogniser ---------- */

interface RecognitionLike {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult:
    | ((e: {
        resultIndex: number;
        results: ArrayLike<{ isFinal: boolean; 0: { transcript: string } }>;
      }) => void)
    | null;
  onspeechstart: (() => void) | null;
  onend: (() => void) | null;
  onerror: ((e: { error?: string }) => void) | null;
  start(): void;
  abort(): void;
}

function browserListener(): Listener {
  let rec: RecognitionLike | undefined;
  return {
    kind: 'browser',
    listen(h = {}) {
      const w = window as unknown as {
        SpeechRecognition?: new () => RecognitionLike;
        webkitSpeechRecognition?: new () => RecognitionLike;
      };
      const C = w.SpeechRecognition ?? w.webkitSpeechRecognition;
      if (!C) return Promise.resolve('');
      return new Promise((resolve, reject) => {
        let final = '';
        rec = new C();
        rec.continuous = false;
        rec.interimResults = true;
        rec.lang = 'en-GB';
        h.onPhase?.('waiting');
        rec.onspeechstart = () => h.onPhase?.('hearing');
        rec.onresult = (e) => {
          let interim = '';
          for (let i = e.resultIndex; i < e.results.length; i++) {
            const r = e.results[i]!;
            if (r.isFinal) final += r[0].transcript;
            else interim += r[0].transcript;
          }
          h.onInterim?.((final + interim).trim());
        };
        rec.onerror = (e) => {
          rec = undefined;
          if (e.error === 'no-speech' || e.error === 'aborted') resolve(final.trim());
          else reject(new Error(e.error ?? 'speech recognition failed'));
        };
        rec.onend = () => {
          rec = undefined;
          resolve(final.trim());
        };
        rec.start();
      });
    },
    cancel() {
      rec?.abort();
      rec = undefined;
    },
  };
}

/* ---------- Recording for the server's Whisper ---------- */

function serverListener(
  microphone?: () => MediaStream | undefined,
  inXr?: () => boolean,
): Listener {
  let own: MediaStream | undefined;
  let audio: AudioContext | undefined;
  let stopCurrent: (() => void) | undefined;
  return {
    kind: 'server',
    async listen(h = {}) {
      // The app's one microphone, as a copy that hears you even while you are muted to the
      // room; a second capture would end the voice chat's on the Quest (2026-10-06).
      const shared = microphone?.()?.getAudioTracks()[0];
      let copy: MediaStreamTrack | undefined;
      let stream: MediaStream;
      if (shared && shared.readyState === 'live') {
        copy = shared.clone();
        copy.enabled = true;
        stream = new MediaStream([copy]);
      } else {
        // Inside VR a prompt would end the session on the Quest (2026-10-06), and the portal
        // already asked: a microphone still closed here was declined.
        if (!own && inXr?.() && (await micMayPrompt()))
          throw new Error('the microphone is not allowed for this site');
        own ??= await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
          video: false,
        });
        stream = own;
      }
      audio ??= new AudioContext();
      if (audio.state === 'suspended') await audio.resume();
      const ctx = audio;
      const rate = ctx.sampleRate;
      const source = ctx.createMediaStreamSource(stream);
      // ScriptProcessor is deprecated but runs everywhere the headset does, without a module.
      const processor = ctx.createScriptProcessor(2048, 1, 1);
      const sink = ctx.createGain();
      sink.gain.value = 0;
      source.connect(processor);
      processor.connect(sink);
      sink.connect(ctx.destination);
      const detector = new SpeechDetector();
      const chunks: Float32Array[] = [];
      const preroll: Float32Array[] = [];
      const prerollChunks = Math.ceil((VAD.prerollMs / 1000) * (rate / 2048));
      h.onPhase?.('waiting');
      const heard = await new Promise<Float32Array | undefined>((resolve) => {
        let finished = false;
        const finish = (keep: boolean) => {
          if (finished) return;
          finished = true;
          processor.onaudioprocess = null;
          source.disconnect();
          processor.disconnect();
          sink.disconnect();
          copy?.stop();
          stopCurrent = undefined;
          if (!keep || !chunks.length) return resolve(undefined);
          const all = new Float32Array(chunks.reduce((n, c) => n + c.length, 0));
          let o = 0;
          for (const c of chunks) {
            all.set(c, o);
            o += c.length;
          }
          resolve(all);
        };
        stopCurrent = () => finish(false);
        processor.onaudioprocess = (e) => {
          const chunk = new Float32Array(e.inputBuffer.getChannelData(0));
          const level = levelOf(chunk);
          h.onLevel?.(Math.min(1, level * 12));
          const event = detector.push(level, (chunk.length / rate) * 1000);
          if (detector.speaking) chunks.push(chunk);
          else {
            preroll.push(chunk);
            if (preroll.length > prerollChunks) preroll.shift();
          }
          if (event === 'start') {
            chunks.unshift(...preroll);
            h.onPhase?.('hearing');
          } else if (event === 'end') finish(true);
          else if (event === 'timeout') finish(false);
        };
      });
      if (!heard) return '';
      h.onPhase?.('transcribing');
      const samples = resample(heard, rate);
      const res = await fetch(api('assistant', 'transcribe'), {
        method: 'POST',
        headers: { 'content-type': 'application/octet-stream' },
        body: samples.buffer as ArrayBuffer,
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `speech to text failed (${res.status})`);
      }
      return ((await res.json()) as { text: string }).text.trim();
    },
    cancel() {
      stopCurrent?.();
    },
  };
}

/**
 * The best way to listen here: the browser's recogniser where it has one, else the server's
 * Whisper when the server offers it, else none (the host then asks for the keyboard).
 */
export function chooseListener(
  serverCanTranscribe: boolean,
  microphone?: () => MediaStream | undefined,
  inXr?: () => boolean,
): Listener | undefined {
  if (dictationAvailable()) return browserListener();
  if (serverCanTranscribe && typeof navigator !== 'undefined' && navigator.mediaDevices)
    return serverListener(microphone, inXr);
  return undefined;
}
